import gsap from "gsap";

class OurJourneyTimeline {
  constructor(element) {
    this.element = element;
    this.menuButtons = element.querySelectorAll(
      ".our-journey__timeline-menu__item",
    );
    this.timelineItems = element.querySelectorAll(
      ".our-journey__timeline-item",
    );
    this.timelineItemsContainer = element.querySelector(
      ".our-journey__timeline-items",
    );
    this.currentIndex = 0;
    this.isAnimating = false;
    this.isMobile = window.innerWidth <= 768;

    this.init();
    this.setupResizeHandler();
  }

  init() {
    if (!this.menuButtons.length || !this.timelineItems.length) return;

    // Set initial state
    this.setInitialState();

    // Store bound handlers for cleanup
    this.menuButtonHandlers = [];

    // Add click listeners to menu buttons
    this.menuButtons.forEach((button) => {
      const handler = (e) => this.handleMenuClick(button);
      this.menuButtonHandlers.push({ button, handler });
      button.addEventListener("click", handler);
    });

    // Update container height based on first item
    this.updateContainerHeight(0);
  }

  setupResizeHandler() {
    let resizeTimeout;
    window.addEventListener("resize", () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.isMobile = window.innerWidth <= 768;
        this.updateContainerHeight(this.currentIndex);
      }, 250);
    });
  }

  setInitialState() {
    // Hide all items except the first one
    this.timelineItems.forEach((item, index) => {
      if (index === 0) {
        gsap.set(item, {
          display: "block",
          opacity: 1,
          y: 0,
        });
      } else {
        gsap.set(item, {
          display: "none",
          opacity: 0,
          y: this.isMobile ? 20 : 30,
        });
      }
    });
  }

  handleMenuClick(clickedButton) {
    if (this.isAnimating) return;

    const newIndex = parseInt(clickedButton.dataset.index);

    // Don't animate if clicking the same item
    if (newIndex === this.currentIndex) return;

    this.isAnimating = true;

    // Update active state on menu buttons
    this.updateActiveMenuButton(newIndex);

    // Animate timeline items (this will also handle container height)
    this.animateTimelineItems(this.currentIndex, newIndex);

    this.currentIndex = newIndex;

    // Reset animation lock after animation completes (adjusted for mobile)
    const lockDuration = this.isMobile ? 600 : 800;
    setTimeout(() => {
      this.isAnimating = false;
    }, lockDuration);
  }

  updateActiveMenuButton(newIndex) {
    this.menuButtons.forEach((button, index) => {
      if (index === newIndex) {
        button.classList.add("active");
      } else {
        button.classList.remove("active");
      }
    });
  }

  animateTimelineItems(oldIndex, newIndex) {
    const oldItem = this.timelineItems[oldIndex];
    const newItem = this.timelineItems[newIndex];

    // Adjust animation parameters based on device
    const slideDistance = this.isMobile ? 20 : 30;
    const fadeDuration = this.isMobile ? 0.8 : 0.8;
    const fadeInDuration = this.isMobile ? 0.5 : 0.5;
    const easeOut = this.isMobile ? "power2.inOut" : "power3.inOut";
    const easeIn = "power2.out";

    // Create timeline for coordinated animations
    const tl = gsap.timeline();

    // First, prepare new item (hidden) to calculate its height
    tl.set(newItem, {
      display: "block",
      opacity: 0,
      y: slideDistance,
    });

    // Update container height based on new item's real height
    tl.call(() => {
      this.updateContainerHeight(newIndex);
    });

    // Fade out and slide up old item
    tl.to(
      oldItem,
      {
        opacity: 0,
        y: -slideDistance,
        duration: fadeDuration,
        ease: easeOut,
        onComplete: () => {
          gsap.set(oldItem, { display: "none" });
        },
      },
      "+=0",
    );

    // Fade in and slide down new item
    tl.to(
      newItem,
      {
        opacity: 1,
        y: 0,
        duration: fadeInDuration,
        ease: easeIn,
      },
      "-=0.3",
    );
  }

  updateContainerHeight(index) {
    const item = this.timelineItems[index];
    const itemHeight = item.scrollHeight;

    // Adjust animation duration for mobile
    const duration = this.isMobile ? 0.6 : 0.6;
    const ease = this.isMobile ? "power3.inOut" : "power3.inOut";

    gsap.to(this.timelineItemsContainer, {
      height: itemHeight,
      duration: duration,
      ease: ease,
    });
  }

  destroy() {
    // Remove event listeners
    if (this.menuButtonHandlers && this.menuButtonHandlers.length) {
      this.menuButtonHandlers.forEach(({ button, handler }) => {
        button.removeEventListener("click", handler);
      });
      this.menuButtonHandlers = [];
    }

    // Kill any running GSAP animations
    if (this.timelineItemsContainer) {
      gsap.killTweensOf(this.timelineItemsContainer);
    }

    if (this.timelineItems && this.timelineItems.length) {
      this.timelineItems.forEach((item) => {
        gsap.killTweensOf(item);
      });
    }

    // Reset properties
    this.element = null;
    this.menuButtons = null;
    this.timelineItems = null;
    this.timelineItemsContainer = null;
    this.menuButtonHandlers = null;
  }
}

// Initialize all timeline instances
const initOurJourneyTimeline = () => {
  const timelines = document.querySelectorAll(".our-journey__timeline");
  timelines.forEach((timeline) => {
    new OurJourneyTimeline(timeline);
  });
};

// Auto-init on DOM ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initOurJourneyTimeline);
} else {
  initOurJourneyTimeline();
}

export default OurJourneyTimeline;
